/**
 * 🤖 AUTO-GENERATED REACT HOOKS - DO NOT EDIT DIRECTLY!
 *
 * This file is automatically generated from entity configuration files.
 * Any manual changes will be lost when the generator runs again.
 *
 * 🔄 DATA FLOW PIPELINE:
 * 1. Entity Config (config/entities/job_notes_junction-entity-config.ts) → Single Source of Truth
 * 2. Generated API Client (generated-job_notes_junction-api-client.ts) → Type-safe API operations
 * 3. THIS FILE → React Query hooks with config-aware field permissions
 * 4. Components → Use these hooks for type-safe data fetching with field restrictions
 *
 * 🛠️ TO MODIFY:
 * - Edit the entity config file: config/entities/job_notes_junction-entity-config.ts
 * - Run: npm run hooks:gen (regenerates this file)
 * - Or run: npm run db:full-workflow (does everything)
 *
 * 📋 FIELD PERMISSIONS (from config):
 * - Searchable fields: none
 * - Filterable fields: id, job_id, note_id, trig_organization_id
 * - Sortable fields: created_at, updated_at
 * - Required fields: job_id, note_id
 * - Readonly fields: id, created_at, updated_at, trig_job_name, trig_job_number, trig_job_status_name, trig_organization_id, trig_note_content, trig_note_title, trig_note_created_by, trig_note_is_internal, trig_note_type
 *
 * 🔒 SECURITY: Only fields marked as 'public: true' in the config are accessible
 * 🎯 VALIDATION: Field-level permissions are enforced in hook parameters
 *
 * 🔗 RELATED FILES:
 * - config/entities/job_notes_junction-entity-config.ts (source configuration)
 * - client/api/generated_clients/generated-job_notes_junction-api-client.ts (API client)
 * - client/api/generated_schemas/job_notes_junction.ts (TypeBox schemas and types)
 *
 * Generated at: 2026-08-20T18:11:43.813Z
 * Source config: config/entities/job_notes_junction-entity-config.ts
 * Public fields: 15/15 total fields
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { JobNotesJunctionClient, JobNotesJunctionQueryOptions } from '../generated_clients/generated-job_notes_junction-api-client.js';
import {
  JobNotesJunction,
  JobNotesJunctionInsert,
  JobNotesJunctionUpdate,
  JobNotesJunctionFieldConfig
} from '../generated_schemas/generated-job_notes_junction-schema.js';

// 🔍 SEARCH/FILTER TYPES MOVED TO BUSINESS LOGIC
//
// Search, filter, and sort types have been moved to business logic hooks
// to maintain clean architectural boundaries.
//
// 📍 For search/filter types, see:
// - client/api/non_generated_business_hooks/job_notes_junction_business_hooks/

// Query keys with enhanced caching strategy
export const jobNotesJunctionKeys = {
  all: ['job_notes_junction'] as const,
  lists: () => [...jobNotesJunctionKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) => [...jobNotesJunctionKeys.lists(), { filters }] as const,
  details: () => [...jobNotesJunctionKeys.all, 'detail'] as const,
  detail: (id: string) => [...jobNotesJunctionKeys.details(), id] as const,
  search: (query: string, fields?: string[]) => [...jobNotesJunctionKeys.all, 'search', query, fields] as const,
  filter: (filters: Record<string, any>) => [...jobNotesJunctionKeys.all, 'filter', filters] as const,
  sort: (sortBy: string, ascending: boolean) => [...jobNotesJunctionKeys.all, 'sort', sortBy, ascending] as const,
  count: (filters?: Record<string, any>) => [...jobNotesJunctionKeys.all, 'count', filters] as const,
};

// Client instance hook - uses the shared httpClient
export const useJobNotesJunctionClient = () => {
  // Return a new instance of the client that uses the shared httpClient
  return new JobNotesJunctionClient();
};

/**
 * Hook to get all job_notes_junction with optional filtering and sorting
 *
 * @param options - Query options with field-level permission validation
 * @returns React Query result with job_notes_junction data
 */
export const useJobNotesJunctionList = (options?: JobNotesJunctionQueryOptions) => {
  const client = useJobNotesJunctionClient();

  return useQuery({
    queryKey: jobNotesJunctionKeys.list(options?.filters || {}),
    queryFn: () => client.getAll(options),
    // Always reconcile with the server on mount, even when persisted/cached
    // data is restored (field PWA offline cache) or a cross-device write
    // happened elsewhere. Cached data still paints instantly; the refetch
    // corrects it. Offline, the refetch simply fails and the cache is kept.
    // Nobody should ever see stale operational data. (card rKkWHT_XhbXU)
    staleTime: 0,
    refetchOnMount: 'always',
  });
};

/**
 * Hook to get a single job_notes_junctio by ID
 *
 * @param id - The job_notes_junctio ID
 * @returns React Query result with single job_notes_junctio data
 */
export const useJobNotesJunction = (id: string) => {
  const client = useJobNotesJunctionClient();

  return useQuery({
    queryKey: jobNotesJunctionKeys.detail(id),
    queryFn: () => client.getById(id),
    enabled: !!id,
    // Always reconcile with the server on mount so a reload / navigation never
    // paints a stale restored (persisted) detail snapshot. (card rKkWHT_XhbXU)
    staleTime: 0,
    refetchOnMount: 'always',
  });
};

/**
 * 🔍 SEARCH/FILTER OPERATIONS MOVED TO BUSINESS LOGIC
 *
 * Search and filter operations have been moved to business logic hooks
 * to maintain clean architectural boundaries:
 *
 * - Generated clients = Pure CRUD only (GET, POST, PUT, DELETE, COUNT)
 * - Search/Filter = Complex business logic in /api/business/job_notes_junction
 *
 * 📍 For search functionality, use:
 * - client/api/non_generated_business_hooks/job_notes_junction_business_hooks/
 */

/**
 * Hook to create a new job_notes_junctio
 *
 * ⚠️ FIELD RESTRICTIONS: Required fields: job_id, note_id
 * ⚠️ READONLY FIELDS: These fields cannot be set: id, created_at, updated_at, trig_job_name, trig_job_number, trig_job_status_name, trig_organization_id, trig_note_content, trig_note_title, trig_note_created_by, trig_note_is_internal, trig_note_type
 *
 * @returns React Query mutation for creating job_notes_junctio
 */
export const useCreateJobNotesJunction = () => {
  const client = useJobNotesJunctionClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: JobNotesJunctionInsert) => client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobNotesJunctionKeys.all });
    },
  });
};

/**
 * Hook to update an existing job_notes_junctio
 *
 * ⚠️ READONLY FIELDS: These fields cannot be updated: id, created_at, updated_at, trig_job_name, trig_job_number, trig_job_status_name, trig_organization_id, trig_note_content, trig_note_title, trig_note_created_by, trig_note_is_internal, trig_note_type
 *
 * @returns React Query mutation for updating job_notes_junctio
 */
export const useUpdateJobNotesJunction = () => {
  const client = useJobNotesJunctionClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: JobNotesJunctionUpdate }) =>
      client.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: jobNotesJunctionKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: jobNotesJunctionKeys.lists() });
    },
  });
};

/**
 * Hook to delete a job_notes_junctio
 *
 * @returns React Query mutation for deleting job_notes_junctio
 */
export const useDeleteJobNotesJunction = () => {
  const client = useJobNotesJunctionClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobNotesJunctionKeys.all });
    },
  });
};

/**
 * Hook to count job_notes_junction with optional filtering
 *
 * ⚠️ FIELD RESTRICTIONS: Only filterable fields can be used: id, job_id, note_id, trig_organization_id
 *
 * @param filter - Optional filter using only filterable fields
 * @returns React Query result with count data
 */
export const useJobNotesJunctionCount = (filter?: Partial<Record<typeof JobNotesJunctionFieldConfig.filterable[number], any>>) => {
  const client = useJobNotesJunctionClient();
  
  return useQuery({
    queryKey: jobNotesJunctionKeys.count(filter),
    queryFn: () => client.count(filter),
  });
};

/**
 * Hook to get field configuration metadata
 *
 * @returns Field configuration with permission information
 */
export const useJobNotesJunctionFieldConfig = () => {
  return JobNotesJunctionFieldConfig;
};